import { notNull } from "@msys/common";
import { TFunction } from "@msys/tolgee";
import { Props2 } from "../../../clients/graphqlTypes.js";
import {
  isBooleanType,
  isComputedProp,
  isNumberType,
  isPropHavingRangeDefined,
  isTextType,
} from "../doc-items/properties.js";

export function renderAllowedValues(
  prop: Props2,
  t: TFunction<"Global">,
  getNumberLabel: (value: number, step?: number) => string,
  getBoolLabel: (value: boolean) => string,
  allowedValuesExclude?: string[] | number[] | undefined,
  allowedValuesMedia?:
    | {
        valueAsString: string;
        media: { url: string } | null;
      }[]
    | undefined
) {
  if (isComputedProp(prop)) return null;
  if (isNumberType(prop) && isPropHavingRangeDefined(prop)) {
    const min = prop.range?.min ?? null;
    const max = prop.range?.max ?? null;
    const step = prop.step ?? null;
    return [
      min !== null && max !== null
        ? `${t("Range", { ns: "Global" })}: ${getNumberLabel(min)}–${getNumberLabel(max)}`
        : min !== null
          ? `${t("Min", { ns: "Global" })}: ${getNumberLabel(min)}`
          : max !== null
            ? `${t("Max", { ns: "Global" })}: ${getNumberLabel(max)}`
            : null,
      step !== null ? `Step: ${step}` : null,
    ]
      .filter(notNull)
      .join(", ");
  }
  if (isBooleanType(prop)) {
    return (
      [true, false]
        .filter(value => {
          const valueOverride = allowedValuesMedia?.find(
            avm => avm.valueAsString === value.toString()
          );
          if (valueOverride) {
            return Boolean(valueOverride.media);
          }
          const valueProperty = prop.valuesMediaBool?.find(
            vmb => vmb.bool === value
          );
          if (valueProperty) {
            return Boolean(valueProperty.media);
          }
          return false;
        })
        .map(value => getBoolLabel(value))
        .join(" / ") || null
    );
  }
  if (isTextType(prop)) {
    return (
      prop.allowedValuesText
        .filter(
          av =>
            !allowedValuesExclude ||
            !(allowedValuesExclude as string[]).includes(av.allowedText)
        )
        .map(av => av.allowedText)
        .join(", ") || null
    );
  }
  if (isNumberType(prop)) {
    return (
      prop.allowedValuesNumber
        .filter(
          av =>
            !allowedValuesExclude ||
            !(allowedValuesExclude as number[]).includes(av.allowedNumber)
        )
        .map(av => getNumberLabel(av.allowedNumber))
        .join(", ") || null
    );
  }
  return null;
}

export function hasAllowedValuesToOverride(prop: Props2): boolean {
  if (isComputedProp(prop)) return false;
  if (isBooleanType(prop)) return true;
  if (isTextType(prop)) return prop.allowedValuesText.length > 0;
  if (isNumberType(prop)) return prop.allowedValuesNumber.length > 0;
  return false;
}
